import { supabase } from "./supabase";
import type { CustomerOrigin } from "@yatta/domain";
import { env } from "./env";
import { createFakeCustomerRecord } from "../data/fake-customer-store";

function asError(value: unknown): Error {
  return value instanceof Error ? value : new Error("Não foi possível cadastrar o cliente.");
}

export type ManualCustomerFamilyInput = {
  organizationId: string;
  displayName?: string;
  sellerId?: string;
  customerOrigin: CustomerOrigin;
  titular: {
    fullName: string;
    cpf: string;
    preferredName?: string;
    birthDate?: string;
    contacts?: Array<{
      kind: "EMAIL" | "PHONE" | "WHATSAPP";
      value: string;
      masked?: string;
      isPrimary?: boolean;
    }>;
  };
  dependents?: Array<{
    fullName: string;
    cpf?: string;
    preferredName?: string;
    birthDate?: string;
    relationship?: string;
  }>;
  address?: {
    street: string;
    number?: string;
    complement?: string;
    neighborhood?: string;
    city: string;
    state: string;
    postalCode?: string;
    proximity?: string;
  };
};

export async function createManualCustomerFamily(input: ManualCustomerFamilyInput) {
  const response = await supabase.functions.invoke("manual-customer", { body: input });
  const error: unknown = response.error;
  if (error) throw asError(error);
  const data: unknown = response.data;
  return data as {
    customerAccountId: string;
    householdId: string;
    primaryPersonId: string;
    customerOrigin: CustomerOrigin;
    ownerSellerId: string;
    registrationStatus: string;
    contractStatus: string;
    accessStatus: string;
    benefitStatus: string;
    requestId: string;
  };
}

type CustomerCommandInput = {
  holder: { fullName: string; cpf: string; birthDate?: string; phone?: string; email?: string };
  dependents?: Array<{ fullName: string; relationship?: string }>;
  sellerId?: string;
  customerOrigin: CustomerOrigin;
  address?: {
    street?: string;
    number?: string;
    complement?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
  /** Client-side selector from the authenticated active organization; never authorization proof. */
  organizationId: string;
};

/** Compatibility adapter for the manual customer form; the canonical API is createManualCustomerFamily. */
export async function createCustomerCommand(input: CustomerCommandInput) {
  if (env.useMocks) {
    const record = createFakeCustomerRecord({
      fullName: input.holder.fullName,
      cpf: input.holder.cpf,
      phone: input.holder.phone ?? "",
      sellerId: input.sellerId,
      customerOrigin: input.customerOrigin,
      dependentCount: input.dependents?.length ?? 0,
    });
    return {
      customerAccountId: record.id,
      householdId: record.id,
      primaryPersonId: `${record.id}-holder`,
      customerOrigin: record.customerOrigin,
      ownerSellerId: input.sellerId ?? "",
      registrationStatus: record.registrationStatus,
      contractStatus: record.contractStatus,
      accessStatus: record.accessStatus,
      benefitStatus: record.benefitStatus,
      requestId: `${record.id}-request`,
    };
  }
  if (!input.organizationId) throw new Error("Selecione uma organização ativa antes de cadastrar.");
  return createManualCustomerFamily({
    organizationId: input.organizationId,
    sellerId: input.sellerId,
    customerOrigin: input.customerOrigin,
    titular: {
      fullName: input.holder.fullName,
      cpf: input.holder.cpf,
      birthDate: input.holder.birthDate,
      contacts: [
        ...(input.holder.phone
          ? [{ kind: "PHONE" as const, value: input.holder.phone, isPrimary: true }]
          : []),
        ...(input.holder.email ? [{ kind: "EMAIL" as const, value: input.holder.email }] : []),
      ],
    },
    dependents: input.dependents,
    ...(input.address ? { address: input.address as ManualCustomerFamilyInput["address"] } : {}),
  });
}
